import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { attachmentSrc, downloadAttachment, openAttachment, MEDIA_LABELS } from "@/lib/files";
import { Paperclip, Download, Loader2, AlertTriangle, RotateCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const INLINE = ["image", "sticker", "audio", "video"];

function useBlobUrl(src, enabled) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!enabled || !src) return undefined;
    let alive = true;
    let objUrl = null;
    api.get(src, { responseType: "blob" })
      .then(({ data }) => { if (alive) { objUrl = URL.createObjectURL(data); setUrl(objUrl); } })
      .catch(() => {});
    return () => { alive = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [src, enabled]);
  return url;
}

function MediaPreview({ type, url, name }) {
  const [broken, setBroken] = useState(false);
  if (!url) return <div className="w-48 h-24 rounded-lg bg-black/10 animate-pulse" />;
  if (broken) return <div className="text-xs opacity-70">Pré-visualização indisponível — use Baixar</div>;
  if (type === "image" || type === "sticker")
    return <img src={url} alt={name} onError={() => setBroken(true)} className="max-w-[260px] max-h-[260px] rounded-lg object-contain" data-testid="attachment-image" />;
  if (type === "audio") return <audio controls src={url} onError={() => setBroken(true)} className="max-w-[260px]" data-testid="attachment-audio" />;
  return <video controls src={url} onError={() => setBroken(true)} className="max-w-[300px] max-h-[240px] rounded-lg" data-testid="attachment-video" />;
}

export const AttachmentView = ({ message }) => {
  const status = message.media_status || (attachmentSrc(message) ? "ok" : "missing");
  const src = attachmentSrc(message);
  const name = message.media_name || MEDIA_LABELS[message.media_type] || "Arquivo";
  const url = useBlobUrl(src, status === "ok" && INLINE.includes(message.media_type));
  const [busy, setBusy] = useState(false);

  const retry = async () => {
    setBusy(true);
    try { await api.post(`/attachments/${message.attachment_id}/retry`); toast.info("Baixando arquivo novamente…"); }
    catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Não foi possível tentar novamente agora");
    }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-1.5" data-testid="chat-attachment">
      <div className="flex items-center gap-1.5 text-xs font-semibold opacity-90">
        <Paperclip className="w-3 h-3 shrink-0" />
        <span className="truncate max-w-[220px]">{MEDIA_LABELS[message.media_type] || "Arquivo"} · {name}</span>
      </div>
      {status === "ok" && INLINE.includes(message.media_type) && <MediaPreview type={message.media_type} url={url} name={name} />}
      {status === "pending" && (
        <div className="flex items-center gap-1.5 text-xs opacity-80"><Loader2 className="w-3 h-3 animate-spin" /> Recebendo arquivo…</div>
      )}
      {(status === "failed" || status === "too_large") && (
        <div className="flex items-center gap-2 text-xs">
          <AlertTriangle className="w-3 h-3 text-rose-500" />
          <span>{status === "too_large" ? "Arquivo acima do limite" : "Falha ao receber"}</span>
          {status === "failed" && message.attachment_id && (
            <button onClick={retry} disabled={busy} className="underline font-semibold inline-flex items-center gap-1" data-testid="attachment-retry-button">
              <RotateCw className="w-3 h-3" /> Tentar novamente
            </button>
          )}
        </div>
      )}
      {status === "missing" && message.attachment_id && (
        <div className="flex items-center gap-2 text-xs">
          <AlertTriangle className="w-3 h-3 text-rose-500" />
          <span>Arquivo indisponível — peça ao cliente que reenvie</span>
          {message.attachment_id && (
            <button onClick={retry} disabled={busy} className="underline font-semibold inline-flex items-center gap-1" data-testid="attachment-missing-retry-button">
              <RotateCw className="w-3 h-3" /> Tentar recuperar
            </button>
          )}
        </div>
      )}
      {status === "missing" && !message.attachment_id && <div className="text-xs opacity-70">Recebido antes da correção — arquivo indisponível</div>}
      {status === "ok" && src && (
        <div className="flex gap-3 text-xs font-semibold">
          {!INLINE.includes(message.media_type) && (
            <button onClick={() => openAttachment(src).catch(() => toast.error("Não foi possível abrir"))} className="inline-flex items-center gap-1 underline" data-testid="attachment-open-button">
              <ExternalLink className="w-3 h-3" /> Abrir
            </button>
          )}
          <button onClick={() => downloadAttachment(src, name).catch(() => toast.error("Falha no download"))} className="inline-flex items-center gap-1 underline" data-testid="attachment-download-button">
            <Download className="w-3 h-3" /> Baixar
          </button>
        </div>
      )}
    </div>
  );
};
